import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';

import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { ConfirmationModalType } from 'app/shared/components/confirmation-modal';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { DocumentItem } from '../../interfaces/document-item.interface';
import { LineAltUnits } from '../../interfaces/dialog-data.interface';
import { altUnitsOf } from '../../alt-units.util';
import { DgNumberPipe } from '../../dg-number.pipe';
import { BomCellComponent } from '../bom-cell/bom-cell.component';
import { BonusCellComponent } from '../bonus-cell/bonus-cell.component';

@Component({
  selector: 'app-items-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatMenuModule,
    MatBadgeModule,
    DgNumberPipe,
    BomCellComponent,
    BonusCellComponent,
    TranslatePipe,
  ],
  templateUrl: './items-table.component.html',
  styleUrl: './items-table.component.scss',
})
export class ItemsTableComponent {
  @Input() items: DocumentItem[] = [];
  @Input() embedded = false;
  @Input() canAdd = true;
  @Input() editablePrice = false; // canUpdatePrices
  @Input() taxIncluded = false; // company.isTaxIncluded (precio con impuesto → subtotal neto)
  @Input() compact = false; // por debajo de md: tarjetas en lugar de tabla
  @Input() showSuggestions = false; // el panel de sugerencias se abre desde aquí
  @Input() searchResults: any[] = []; // opciones del autocomplete de búsqueda de artículos
  @Input() searchLoading = false; // la búsqueda remota está en curso (skeleton en el dropdown)
  @Input() removeLeftZeros = false; // la clase de documento pide mostrar el código sin ceros a la izquierda
  @Input() allowMultipleWarehouses = false; // la clase permite repartir la línea entre almacenes
  @Input() splitsByWarehouse = false; // la clase separa pedidos por almacén
  @Input() umpActive = true; // company.UMPActive (la unidad paralela también depende del artículo)

  @Output() add = new EventEmitter<string>();
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() selectResult = new EventEmitter<any>();
  @Output() openSearch = new EventEmitter<void>();
  @Output() expand = new EventEmitter<void>();
  @Output() openSuggestions = new EventEmitter<void>();
  @Output() changeUnit = new EventEmitter<DocumentItem>();
  @Output() quantityChange = new EventEmitter<DocumentItem>();
  @Output() priceChange = new EventEmitter<DocumentItem>();
  @Output() remove = new EventEmitter<DocumentItem>();
  @Output() openCalculator = new EventEmitter<DocumentItem>();
  @Output() openDetail = new EventEmitter<DocumentItem>();
  @Output() openWarehouses = new EventEmitter<DocumentItem>();

  searchValue = '';
  selected = new Set<string>();
  private justPicked = false; // evita que el Enter que selecciona una opción dispare también submit()

  // separando por almacén la línea agrupa varios: el almacén se comunica con el
  // distintivo del botón y no como texto, que ahí sería engañoso
  warehouseLabel(item: DocumentItem): string {
    if (this.splitsByWarehouse) return '';
    const code = item.warehouseCode ?? '';
    const name = item.warehouseName ?? '';
    return code && name ? `${code} - ${name}` : code || name;
  }

  // cuántos almacenes abastecen la línea; el botón lo muestra como distintivo
  warehouseCount(item: DocumentItem): number {
    return item.allocations?.length ?? 0;
  }

  // contador de notas + adjuntos por línea (subidos y nuevos)
  detailCount(item: DocumentItem): number {
    return (item.note ? 1 : 0) + (item.evidences?.length ?? 0) + (item.newAttachments?.length ?? 0);
  }

  // marca el menú agrupado cuando alguna de sus acciones tiene algo que mostrar
  rowActionsBadge(item: DocumentItem): string | null {
    return this.detailCount(item) > 0 || this.warehouseCount(item) > 1 ? '•' : null;
  }

  // N° de línea: entero consecutivo, el mismo que se envía como position (las de regalo también cuentan)
  displayNumber(item: DocumentItem): string {
    return String(this.items.indexOf(item) + 1);
  }

  constructor(
    private confirmationService: ConfirmationService,
    private readonly i18n: LanguageService,
  ) {}

  // Enter en el campo: el que confirma una opción del dropdown no debe abrir además la búsqueda
  submitFromEnter(): void {
    if (this.justPicked) {
      this.justPicked = false;
      return;
    }
    this.submit();
  }

  // botón "Agregar": siempre actúa (el descarte del Enter no aplica al clic)
  submit(): void {
    this.justPicked = false;
    this.add.emit(this.searchValue.trim());
    this.searchValue = '';
  }

  // el usuario escribe: se delega la búsqueda remota al componente padre
  // (al seleccionar una opción el ngModel recibe el objeto material; se ignora ese caso)
  onSearchInput(value: unknown): void {
    if (typeof value !== 'string') return;
    const term = value.trim();
    if (term) this.justPicked = false; // volver a escribir reactiva el Enter para buscar
    this.searchTermChange.emit(term);
  }

  // el usuario elige un artículo del dropdown: se agrega y se limpia el campo
  onResultPicked(material: any): void {
    if (!material) return;
    this.justPicked = true;
    this.selectResult.emit(material);
    this.searchValue = '';
  }

  // el input queda vacío tras seleccionar (la opción no debe escribirse en el campo)
  displayNothing = (): string => '';

  resultCode(m: any): string {
    return this.removeLeftZeros ? String(m?.code ?? '').replace(/^0+/, '') : (m?.code ?? '');
  }

  // solo números positivos y decimales (sin letras, símbolos ni espacios)
  onQuantityInput(item: DocumentItem, value: string): void {
    let raw = (value ?? '').replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    }
    item.quantityText = raw;
    item.quantity = raw === '' || raw === '.' ? 0 : parseFloat(raw);
    this.quantityChange.emit(item);
  }

  onQuantityKeydown(e: KeyboardEvent): void {
    const control = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    if (control.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (/^[0-9]$/.test(e.key)) return;
    if (e.key === '.' && !(e.target as HTMLInputElement).value.includes('.')) return;
    e.preventDefault();
  }

  // precio manual (canUpdatePrices): solo números y un punto decimal
  onPriceInput(item: DocumentItem, value: string): void {
    let raw = (value ?? '').replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    if (firstDot !== -1) {
      raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    }
    item.price = raw === '' || raw === '.' ? 0 : parseFloat(raw);
    item.manualPrice = true;
    this.priceChange.emit(item);
  }

  taxLabel(item: DocumentItem): string {
    return item.taxName || (item.taxPct ? item.taxPct + '%' : '-');
  }

  // el artículo tiene unidades alternas para calcular/convertir
  hasAlternalUnits(item: DocumentItem): boolean {
    return (item.material?.alternalUnitOfMeasures ?? []).length > 0;
  }

  // cantidad de venta de la línea convertida a unidad secundaria y paralela;
  // es informativa, se recalcula al teclear la cantidad o cambiar de unidad
  altUnits(item: DocumentItem): LineAltUnits {
    return altUnitsOf(item.material, item.unitMeasureId, item.quantity, this.umpActive);
  }

  // base neta: si el precio ya incluye impuesto, se le resta el ISV
  rowSubtotal(item: DocumentItem): number {
    return item.quantity * item.price - (this.taxIncluded ? item.isv : 0);
  }

  rowTotal(item: DocumentItem): number {
    return this.rowSubtotal(item) - item.discount + item.isv;
  }

  removeRow(item: DocumentItem): void {
    const name = item.name || item.code || this.i18n.t('DOCUMENT_GENERATE.MESSAGES.THIS_ITEM');
    // el bono es un producto de regalo: mensaje distinto para no confundir
    const confirm$ = item.isBonus
      ? this.confirmationService.confirm({
          title: this.i18n.t('DOCUMENT_GENERATE.ITEMS_TABLE.REMOVE_BONUS_TITLE'),
          description: this.i18n.t('DOCUMENT_GENERATE.ITEMS_TABLE.REMOVE_BONUS_MESSAGE', { name }),
          type: ConfirmationModalType.WARNING,
          confirmButtonText: this.i18n.t('DOCUMENT_GENERATE.ITEMS_TABLE.REMOVE_TOOLTIP'),
          cancelButtonText: this.i18n.t('GENERAL.CANCEL'),
          descriptionIcon: 'redeem',
        })
      : this.confirmationService.confirmDelete(name);
    confirm$.subscribe((ok) => {
      if (!ok) return;
      this.selected.delete(item.lineId);
      this.remove.emit(item);
    });
  }

  // selección masiva (solo modo embebido)
  isSelected(item: DocumentItem): boolean {
    return this.selected.has(item.lineId);
  }

  toggle(item: DocumentItem): void {
    this.selected.has(item.lineId) ? this.selected.delete(item.lineId) : this.selected.add(item.lineId);
  }

  allSelected(): boolean {
    return this.items.length > 0 && this.items.every((i) => this.selected.has(i.lineId));
  }

  someSelected(): boolean {
    return this.selected.size > 0 && !this.allSelected();
  }

  toggleAll(checked: boolean): void {
    if (checked) {
      this.items.forEach((i) => this.selected.add(i.lineId));
    } else {
      this.selected.clear();
    }
  }

  removeSelected(): void {
    const toRemove = this.items.filter((i) => this.selected.has(i.lineId));
    if (!toRemove.length) return;
    this.confirmationService
      .confirm({
        title: this.i18n.t('DOCUMENT_GENERATE.ITEMS_TABLE.REMOVE_ITEMS_TITLE'),
        description: this.i18n.t('DOCUMENT_GENERATE.ITEMS_TABLE.REMOVE_ITEMS_MESSAGE', {
          count: toRemove.length,
        }),
        type: ConfirmationModalType.DANGER,
        confirmButtonText: this.i18n.t('DOCUMENT_GENERATE.ITEMS_TABLE.REMOVE_TOOLTIP'),
        cancelButtonText: this.i18n.t('GENERAL.CANCEL'),
        descriptionIcon: 'delete',
      })
      .subscribe((ok) => {
        if (!ok) return;
        for (const item of toRemove) {
          this.remove.emit(item);
        }
        this.selected.clear();
      });
  }
}
